import type { KeyboardEvent, ReactNode, SyntheticEvent } from "react";
import {
  Icon,
  MediaPreview,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaOpportunityDisplayRow } from "../../../lib/persona-opportunity-row-model.js";

export interface PersonaOpportunitySupportingMediaProps {
  row: PersonaOpportunityDisplayRow;
  /** Visual size variant — card hero, drawer portrait, or compact table cell. */
  variant: "card" | "drawer" | "table";
  className?: string;
  children?: ReactNode;
}

function PlayGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function CompetitorBrandStrip({
  row,
  variant,
}: {
  row: PersonaOpportunityDisplayRow;
  variant: "card" | "drawer" | "table";
}) {
  const { t } = useTranslation();
  if (!row.competitorName || !row.competitorInitial) return null;

  const isCard = variant !== "table";

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 bottom-0 z-[1] flex items-end gap-1.5",
        isCard ? "bg-linear-to-t from-black/75 via-black/35 to-transparent px-2 pb-2 pt-8" : "p-0.5",
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20 font-semibold text-white ring-1 ring-white/35 backdrop-blur-sm",
          isCard ? "size-7 text-[11px]" : "size-4 text-[8px]",
        )}
        aria-hidden
      >
        {row.competitorProfilePictureUrl ? (
          <img
            src={row.competitorProfilePictureUrl}
            alt=""
            className="size-full object-cover"
          />
        ) : (
          row.competitorInitial
        )}
      </span>
      {isCard ? (
        <div className="flex min-w-0 flex-col pb-0.5">
          <p className="truncate text-[12px] font-semibold leading-tight text-white">
            {row.competitorName}
          </p>
          {row.daysLive != null ? (
            <p className="truncate text-[11px] leading-tight text-white/80">
              {t("personaOpportunities.active.daysLive", {
                count: row.daysLive,
                defaultValue: "{{count}}d live",
              })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Supporting-ad thumb with MediaPreview lightbox when `mediaUrl` is present.
 * Without a media URL, shows a disabled play affordance (plan R: graceful no-CDN case).
 */
export function PersonaOpportunitySupportingMedia({
  row,
  variant,
  className,
  children,
}: PersonaOpportunitySupportingMediaProps) {
  const { t } = useTranslation();
  const hasMedia = Boolean(row.mediaUrl);
  const hasThumbnail = Boolean(row.thumbnailUrl);
  const playUnavailable = t(
    "personaOpportunities.active.playUnavailable",
    "Video unavailable for this opportunity.",
  );
  const daysMeta =
    row.daysLive != null
      ? t("personaOpportunities.active.daysLive", {
          count: row.daysLive,
          defaultValue: "{{count}}d live",
        })
      : null;
  const lightboxTitle = row.competitorName ?? row.angle;
  const lightboxMeta = daysMeta ? <span>{daysMeta}</span> : undefined;

  const isCompact = variant === "table";
  const shellClass = isCompact
    ? "h-16 w-12 overflow-hidden rounded-sm bg-muted"
    : variant === "drawer"
      ? "aspect-[9/16] w-[360px] max-w-full overflow-hidden rounded-sm bg-muted"
      : "aspect-[9/16] size-full overflow-hidden rounded-sm bg-muted";

  if (!hasThumbnail && !hasMedia) {
    return (
      <div className={cn("relative flex items-center justify-center", shellClass, className)}>
        <Icon name="image" size={isCompact ? "xs" : "md"} className="text-muted-foreground" />
        <CompetitorBrandStrip row={row} variant={variant} />
        {children}
      </div>
    );
  }

  if (!hasMedia) {
    return (
      <div className={cn("relative", shellClass, className)}>
        {hasThumbnail ? (
          <img src={row.thumbnailUrl!} alt="" className="size-full object-cover" />
        ) : null}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "absolute inset-0 z-[2] flex items-center justify-center",
                  isCompact && "bg-black/10",
                )}
                aria-label={playUnavailable}
              >
                <span
                  className={cn(
                    "flex items-center justify-center rounded-full bg-black/35 text-white/50 ring-1 ring-white/20",
                    isCompact ? "size-6" : "size-10",
                  )}
                >
                  <PlayGlyph className={isCompact ? "size-3" : "size-5"} />
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{playUnavailable}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <CompetitorBrandStrip row={row} variant={variant} />
        {children}
      </div>
    );
  }

  return (
    // The lightbox portals out of the DOM but React still bubbles its events through this
    // tree, so both the thumb and the open lightbox would otherwise trigger row/card clicks.
    <span className="contents" onClick={stopPropagation} onKeyDown={stopActivationKeys}>
      <MediaPreview
        src={row.mediaUrl!}
        thumbnailSrc={row.thumbnailUrl ?? ""}
        type="video"
        alt={lightboxTitle}
        title={lightboxTitle}
        logoSrc={row.competitorProfilePictureUrl ?? undefined}
        meta={lightboxMeta}
        className={cn("group", shellClass, className)}
      >
        <div className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0 motion-reduce:transition-none">
          <span
            className={cn(
              "flex items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]",
              isCompact ? "size-6" : "size-10",
            )}
          >
            <PlayGlyph className={isCompact ? "size-3" : "size-5"} />
          </span>
        </div>
        <CompetitorBrandStrip row={row} variant={variant} />
        {children}
      </MediaPreview>
    </span>
  );
}

function stopPropagation(event: SyntheticEvent) {
  event.stopPropagation();
}

/** Only Enter/Space — Escape and other keys must still reach the lightbox and page. */
function stopActivationKeys(event: KeyboardEvent<HTMLSpanElement>) {
  if (event.key === "Enter" || event.key === " ") event.stopPropagation();
}
